<script lang="ts">
  import { on } from 'shared/javascripts/util/events'
  import abTests from 'frontend/shared/javascripts/modules/ab_tests'
  import { trackInteraction } from 'frontend/shared/javascripts/tracking'
  import session from 'frontend/shared/javascripts/util/session'
  import type PlayerClass from 'player/javascripts/player'
  import campaignIcon from 'frontend/shared/images/player/pre_ad_screen/campaign.svg'
  import claim from 'frontend/shared/images/player/pre_ad_screen/claim.svg'
  import flyingSymbols from 'frontend/shared/images/homepage/flying_symbols.svg'
  import chevronIcon from 'shared/images/icons/chevron-right.svg'
  import pupilImage from 'frontend/shared/images/player/pre_ad_screen/pupil.webp'
  import studentImage from 'frontend/shared/images/player/pre_ad_screen/student.webp'
  import traineeImage from 'frontend/shared/images/player/pre_ad_screen/trainee.webp'

  type Role = 'pupil' | 'student' | 'trainee'

  interface RoleCard {
    role: Role
    label: string
    image: string
  }

  const ROLES: RoleCard[] = [
    { role: 'pupil', label: 'Schüler/in', image: pupilImage },
    { role: 'student', label: 'Studierende/r', image: studentImage },
    { role: 'trainee', label: 'Azubi', image: traineeImage },
  ]

  let {
    container,
    player,
    element = $bindable(),
    shown = $bindable(false),
    contentHeight = $bindable(0),
  }: {
    /** The player container, which the player emits its `app:player:*` events on. */
    container?: HTMLElement
    player: Promise<InstanceType<typeof PlayerClass>>
    element?: HTMLElement
    shown?: boolean
    /** Reported to the host: how tall the screen's content is, so a short player can make room for it. */
    contentHeight?: number
  } = $props()

  let remainingSeconds = $state(0)
  let selected = $state<Role | null>(null)
  let tracked = false

  // Followed rather than read once: Convert may decide after the video has loaded.
  $effect(() =>
    abTests.onVariant('video_pre_ad_role_selection', (variant) => {
      if (variant === 'b') {
        void player.then((instance) => instance.activatePreAdScreen())
      }
    }),
  )

  // Docked (PiP) or sticky on mobile: the stylesheet hides the screen there, so the player must not announce
  // the ad for it.
  function floating(): boolean {
    return !!element?.closest('.player.-docked, .player-toggle-content.-sticky')
  }

  function update(event: Event & { shown: boolean; remainingSeconds: number }): void {
    remainingSeconds = event.remainingSeconds
    shown = event.shown
    if (shown && !tracked && element) {
      tracked = true
      // tracking: player.pre_ad_screen_shown
      trackInteraction(
        'element.visible',
        { id: 'pre_ad_screen_shown', text: 'Sag uns, wer du bist' },
        element,
      )
    }
  }

  function end(): void {
    shown = false
  }

  $effect(() => {
    if (!container) {
      return
    }

    const unsubscribes = [
      on(container, 'app:player:preAdScreenRequested', (event: Event) => {
        // A user who has picked a role once is not asked again.
        if (floating() || session.preAdRoleChosen) {
          event.preventDefault()
        }
      }),
      on(container, 'app:player:preAdScreen', update),
      on(container, 'app:player:adStarted app:player:noAdReceived', end),
    ]

    return () => {
      end()
      for (const unsubscribe of unsubscribes) {
        unsubscribe()
      }
    }
  })

  const countdown = $derived(
    `${Math.floor(remainingSeconds / 60)}:${String(remainingSeconds % 60).padStart(2, '0')}`,
  )

  // Only the first choice counts, so the click is tracked here rather than by the button.
  function choose(card: RoleCard, button: HTMLElement): void {
    if (selected) {
      return
    }
    selected = card.role
    session.preAdRoleChosen = true
    // tracking: player.pre_ad_role_click
    trackInteraction(
      'click.element',
      { id: 'pre_ad_role_select', params: { role: card.role } },
      button,
    )
    void player.then((instance) => instance.startAdBreakNow())
  }
</script>

<div
  class="pre-ad-screen"
  class:-visible={shown}
  style={`--symbols-url: url("${flyingSymbols}"); --chevron-url: url("${chevronIcon}")`}
  bind:this={element}
>
  {#if shown}
    <div class="pre-ad-screen--content" bind:clientHeight={contentHeight}>
      <div class="pre-ad-screen--header">
        <img class="pre-ad-screen--icon" src={campaignIcon} alt="" />
        <div>
          <h2 class="pre-ad-screen--title">Sag uns, wer du bist</h2>
          <p class="pre-ad-screen--lead">…und wir spielen dir die passende Zukunftschance aus.</p>
        </div>
      </div>
      <div class="pre-ad-screen--roles">
        {#each ROLES as card (card.role)}
          <button
            type="button"
            class="pre-ad-screen--role"
            class:-selected={selected === card.role}
            onclick={(event) => choose(card, event.currentTarget)}
          >
            <span class="pre-ad-screen--role-image"><img src={card.image} alt="" /></span>
            <span class="pre-ad-screen--role-caption">
              <span class="pre-ad-screen--role-label">{card.label}</span>
              <span class="pre-ad-screen--role-chevron"></span>
            </span>
          </button>
        {/each}
      </div>
    </div>
    <div class="pre-ad-screen--countdown">Werbung in {countdown}</div>
    <img class="pre-ad-screen--claim" src={claim} alt="Deine Zukunft zählt!" />
  {/if}
</div>

<style>
  .pre-ad-screen {
    display: none;
    position: absolute;
    inset: 0;
    z-index: 1100;
    container-type: inline-size;
    overflow: hidden;
    font-family: var(--font-family-base);
    color: var(--color-arsenic);
    background:
      var(--symbols-url) center / 200% auto no-repeat,
      linear-gradient(113deg, #feeff5 0%, #e9daff 93%);
    cursor: default;
  }

  .pre-ad-screen.-visible {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  :global(body:not([data-env='test'])) .pre-ad-screen {
    transition: opacity 175ms;
  }

  @starting-style {
    :global(body:not([data-env='test'])) .pre-ad-screen.-visible {
      opacity: 0;
    }
  }

  :global(.player.-docked) .pre-ad-screen,
  :global(.player-toggle-content.-sticky) .pre-ad-screen {
    display: none;
  }

  .pre-ad-screen--content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 40px;
    box-sizing: border-box;
    width: 100%;
    max-width: 650px;
    padding: 56px 20px;
    text-align: center;
  }

  .pre-ad-screen--header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  .pre-ad-screen--icon {
    width: 64px;
    height: 64px;
  }

  .pre-ad-screen--title {
    margin: 0;
    font-size: 26px;
    font-weight: 600;
    line-height: 1.35;
  }

  .pre-ad-screen--lead {
    margin: 4px 0 0;
    font-size: 18px;
    line-height: 1.55;
  }

  .pre-ad-screen--roles {
    display: flex;
    justify-content: center;
    gap: 20px;
    width: 100%;
  }

  .pre-ad-screen--role {
    display: flex;
    flex-direction: column;
    width: 190px;
    padding: 4px;
    border: 0;
    border-radius: 8px;
    background: var(--color-white);
    box-shadow: var(--box-shadow-1);
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  :global(body:not([data-env='test'])) .pre-ad-screen--role {
    transition:
      transform 150ms,
      box-shadow 150ms;
  }

  @media (hover: hover) {
    .pre-ad-screen--role:hover {
      transform: translateY(-2px);
      box-shadow: var(--box-shadow-floating);
    }
  }

  .pre-ad-screen--role.-selected {
    outline: 2px solid var(--color-cerulean);
    outline-offset: -2px;
  }

  .pre-ad-screen--role-image {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    aspect-ratio: 232 / 154;
    width: 100%;
    border-radius: 4px;
    background: var(--color-primary-brightest);
    overflow: hidden;
  }

  .pre-ad-screen--role-image img {
    width: auto;
    height: 92%;
  }

  .pre-ad-screen--role-caption {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    box-sizing: border-box;
    width: 100%;
    padding: 8px 4px 8px 8px;
  }

  .pre-ad-screen--role-label {
    font-size: 16px;
    font-weight: 600;
    line-height: 1.55;
    white-space: nowrap;
  }

  .pre-ad-screen--role-chevron {
    position: relative;
    flex: 0 0 28px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--color-primary-bright);
  }

  .pre-ad-screen--role-chevron::after {
    content: '';
    position: absolute;
    inset: 2px;
    background-color: var(--color-primary);
    mask: var(--chevron-url) center / 24px no-repeat;
    -webkit-mask: var(--chevron-url) center / 24px no-repeat;
  }

  .pre-ad-screen--countdown {
    position: absolute;
    top: 16px;
    right: 16px;
    padding: 8px;
    border-radius: 2px;
    background: rgba(0, 0, 0, 0.5);
    color: var(--color-white);
    font-size: 16px;
    line-height: 1.3;
    white-space: nowrap;
  }

  .pre-ad-screen--claim {
    position: absolute;
    top: 50%;
    right: 18px;
    width: 86px;
  }

  @container (max-width: 859px) {
    .pre-ad-screen--content {
      padding-bottom: 72px;
    }

    .pre-ad-screen--claim {
      top: auto;
      right: 20px;
      bottom: 20px;
      width: auto;
      height: 44px;
      aspect-ratio: 86 / 87;
      object-fit: cover;
      object-position: top;
    }
  }

  /* Below 660px the three cards no longer fit in one row. */
  @container (max-width: 659px) {
    .pre-ad-screen--content {
      gap: 24px;
      padding: 20px 20px 72px;
    }

    .pre-ad-screen--icon {
      width: 40px;
      height: 40px;
    }

    .pre-ad-screen--title {
      font-size: 18px;
    }

    .pre-ad-screen--lead {
      font-size: 16px;
    }

    .pre-ad-screen--roles {
      flex-direction: column;
      gap: 12px;
    }

    .pre-ad-screen--role {
      flex-direction: row;
      align-items: center;
      gap: 12px;
      width: 100%;
      padding: 4px 8px 4px 4px;
    }

    .pre-ad-screen--role-image {
      flex: 0 0 48px;
      width: 48px;
      height: 48px;
      aspect-ratio: auto;
    }

    .pre-ad-screen--role-image img {
      height: 100%;
    }

    .pre-ad-screen--role-caption {
      flex: 1;
      width: auto;
      padding: 8px 0;
    }

    .pre-ad-screen--countdown {
      top: auto;
      right: auto;
      bottom: 20px;
      left: 20px;
      font-size: 14px;
    }
  }
</style>
